import type { InjectionKey, Ref } from 'vue';
import { ref } from 'vue';

import type { SidebarMenuScope, SidebarMenuScopeConfig } from '../utils/filterMenuTreeByScope';

export type { SidebarMenuScope, SidebarMenuScopeConfig };
export type { SidebarMenuScopeOption } from '../utils/filterMenuTreeByScope';

export const SIDEBAR_MENU_SCOPE_CONFIG_KEY: InjectionKey<SidebarMenuScopeConfig | null> =
  Symbol('sidebarMenuScopeConfig');

const DEFAULT_STORAGE_KEY = 'ax-sidebar-menu-scope';

/** showAllOption=false 면 기본/폴백 스코프는 'all' 이 아니라 첫 옵션이다. */
function defaultScope(config: SidebarMenuScopeConfig): SidebarMenuScope {
  if (config.showAllOption === false) return config.options[0]?.value ?? 'all';
  return 'all';
}

function readStoredScope(config: SidebarMenuScopeConfig): SidebarMenuScope {
  const storageKey = config.storageKey ?? DEFAULT_STORAGE_KEY;
  const raw = localStorage.getItem(storageKey);
  // 'all' 은 showAllOption !== false 일 때만 유효
  if (raw === 'all') return config.showAllOption === false ? defaultScope(config) : 'all';
  // options 안의 값만 유효로 인정
  if (raw !== null && config.options.some((o) => o.value === raw)) return raw;
  return defaultScope(config);
}

/**
 * 관리자 사이드바 메뉴 보기 범위 composable.
 * config.storageKey 기준으로 localStorage 에 유지하며,
 * options + 'all' 안의 값만 유효로 인정한다.
 */
export function useSidebarMenuScope(config: SidebarMenuScopeConfig): {
  scope: Ref<SidebarMenuScope>;
  setScope: (next: SidebarMenuScope) => void;
} {
  const storageKey = config.storageKey ?? DEFAULT_STORAGE_KEY;
  const scope = ref<SidebarMenuScope>(readStoredScope(config));

  function setScope(next: SidebarMenuScope): void {
    // 유효한 값(options + 'all')만 허용. 'all' 은 showAllOption !== false 일 때만.
    const allAllowed = config.showAllOption !== false;
    const valid = (next === 'all' && allAllowed) || config.options.some((o) => o.value === next);
    const resolved = valid ? next : defaultScope(config);
    scope.value = resolved;
    localStorage.setItem(storageKey, resolved);
  }

  return { scope, setScope };
}
